import React, { useEffect, useState } from 'react';
import { Box, Grid, Card, CardContent, Typography, Button, Table, TableBody,
  TableCell, TableHead, TableRow, Chip, CircularProgress, Alert } from '@mui/material';
import { Inventory, ShoppingCart, People, Warning, TrendingUp, Refresh } from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { spAPI } from '../../services/api';
import { toast } from 'react-toastify';
//import DataTable, { StatusChip } from '../../components/common/DataTable';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);

const KPI = ({ label, value, sub, color = '#f59e0b', icon }) => (
  <Card sx={{ height: '100%', borderLeft: `4px solid ${color}` }}>
    <CardContent sx={{ pb: '16px !important' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.7rem' }}>{label}</Typography>
          <Typography variant="h4" fontWeight={800} sx={{ my: 0.5, color: '#0f172a' }}>{value}</Typography>
          {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
        </Box>
        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: color + '18', color }}>{icon}</Box>
      </Box>
    </CardContent>
  </Card>
);

const SparePartsDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = async () => {
    try { setLoading(true); const { data: r } = await spAPI.getDashboard(); setData(r.data); }
    catch { toast.error('Failed to load dashboard'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  if (!data) return <Alert severity="error">Failed to load data</Alert>;

  const s = data.sales || {}; const inv = data.inventory || {}; const cust = data.customers || {};

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={800} sx={{ color: '#0f172a' }}>Spare Parts Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</Typography>
        </Box>
        <Button startIcon={<Refresh />} variant="outlined" onClick={fetch} sx={{ borderColor: '#f59e0b', color: '#f59e0b', '&:hover': { borderColor: '#d97706', bgcolor: '#fffbeb' } }}>Refresh</Button>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} lg={3}><KPI label="Revenue Today" value={fmt(s.revenue_today)} sub={`${s.orders_today || 0} orders`} color="#f59e0b" icon={<TrendingUp />} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><KPI label="Monthly Revenue" value={fmt(s.revenue_month)} sub={`${s.orders_month || 0} orders`} color="#10b981" icon={<ShoppingCart />} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><KPI label="Total SKUs" value={inv.total_products || 0} sub={`${inv.total_units || 0} total units`} color="#6366f1" icon={<Inventory />} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><KPI label="Customers" value={cust.total_customers || 0} sub={`+${cust.new_this_month || 0} this month`} color="#ec4899" icon={<People />} /></Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>Revenue Trend</Typography>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={data.revenue_chart || []}>
                  <defs>
                    <linearGradient id="spGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={v => [fmt(v), 'Revenue']} />
                  <Area type="monotone" dataKey="revenue" stroke="#f59e0b" strokeWidth={2.5} fill="url(#spGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: '100%', border: (data.low_stock_alerts||[]).length > 0 ? '1px solid #fde68a' : 'none' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Warning sx={{ color: '#f59e0b' }} />
                <Typography variant="h6" fontWeight={700}>Low Stock Alerts</Typography>
                {(data.low_stock_alerts||[]).length > 0 && <Chip label={(data.low_stock_alerts||[]).length} color="warning" size="small" />}
              </Box>
              {(data.low_stock_alerts || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">All stock levels are healthy ✓</Typography>
              ) : (data.low_stock_alerts || []).map((item, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, borderBottom: '1px solid #f1f5f9' }}>
                  <Box>
                    <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>{item.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{item.category}</Typography>
                  </Box>
                  <Chip label={`${item.quantity} left`} size="small" color={item.quantity === 0 ? 'error' : 'warning'} />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>Recent Orders</Typography>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>Order #</TableCell><TableCell>Customer</TableCell>
                  <TableCell align="right">Amount</TableCell><TableCell>Status</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {(data.recent_orders || []).map(o => (
                    <TableRow key={o.order_number} hover>
                      <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.8rem' }}>{o.order_number}</TableCell>
                      <TableCell>{o.customer_name || 'Walk-in'}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(o.total_amount)}</TableCell>
                      <TableCell><Chip label={o.status} size="small" color={o.status==='paid'?'success':o.status==='cancelled'?'error':'warning'} sx={{ fontSize: '0.7rem', fontWeight: 700 }} /></TableCell>
                    </TableRow>
                  ))}
                  {!data.recent_orders?.length && <TableRow><TableCell colSpan={4} align="center"><Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No orders yet</Typography></TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>Top Selling Parts</Typography>
              {(data.top_products || []).map((p, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, borderBottom: '1px solid #f1f5f9' }}>
                  <Box>
                    <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>{p.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{p.category} · {p.units_sold} units</Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={700} color="warning.dark">{fmt(p.revenue)}</Typography>
                </Box>
              ))}
              {!data.top_products?.length && <Typography variant="body2" color="text.secondary">No sales data yet</Typography>}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SparePartsDashboard;
